import type { SnapApplicationContext, SnapTenantSolutionContext } from '../types';
import { SnapDenyCodes } from '../types';
import { createSnapError } from '../security/sanitize';

const MAX_CONTEXT_BYTES = 16 * 1024;
const MAX_DEPTH = 4;
const MAX_KEYS = 64;

const AUTHORITATIVE_KEYS = new Set([
  'organisationUid',
  'OrganisationUid',
  'organisationUID',
  'solutionUid',
  'SolutionUid',
  'solutionUID',
  'licenceState',
  'licenseState',
  'capabilityState',
  'supportTarget',
  'authenticatedUser',
  'userUid',
  'UserUid'
]);

export interface SafeContextResult {
  ok: true;
  context: Record<string, unknown>;
  bytes: number;
}

export interface SafeContextFailure {
  ok: false;
  error: ReturnType<typeof createSnapError>;
}

export function sanitizeApplicationContext(
  input: SnapApplicationContext | null | undefined
): SafeContextResult | SafeContextFailure {
  if (input == null) {
    return { ok: true, context: {}, bytes: 2 };
  }
  if (typeof input !== 'object' || Array.isArray(input)) {
    return {
      ok: false,
      error: createSnapError(
        'context_invalid',
        SnapDenyCodes.ContextInvalid,
        'Application context must be a plain object.'
      )
    };
  }

  const seen = new WeakSet<object>();
  const out: Record<string, unknown> = {};
  let keyCount = 0;

  const walk = (value: unknown, depth: number): unknown => {
    if (value == null) return null;
    const t = typeof value;
    if (t === 'string' || t === 'number' || t === 'boolean') return value;
    if (t === 'function') {
      throw createSnapError(
        'context_invalid',
        SnapDenyCodes.ContextInvalid,
        'Functions are not allowed in application context.'
      );
    }
    if (typeof Node !== 'undefined' && value instanceof Node) {
      throw createSnapError(
        'context_invalid',
        SnapDenyCodes.ContextInvalid,
        'DOM nodes are not allowed in application context.'
      );
    }
    if (t !== 'object') {
      throw createSnapError(
        'context_invalid',
        SnapDenyCodes.ContextInvalid,
        'Unsupported context value type.'
      );
    }
    if (seen.has(value as object)) {
      throw createSnapError(
        'context_invalid',
        SnapDenyCodes.ContextInvalid,
        'Circular values are not allowed in application context.'
      );
    }
    if (depth > MAX_DEPTH) {
      throw createSnapError(
        'context_invalid',
        SnapDenyCodes.ContextInvalid,
        'Application context exceeds maximum depth.'
      );
    }
    seen.add(value as object);
    if (Array.isArray(value)) {
      return value.slice(0, 50).map((v) => walk(v, depth + 1));
    }
    const obj = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      if (AUTHORITATIVE_KEYS.has(k)) continue;
      keyCount += 1;
      if (keyCount > MAX_KEYS) {
        throw createSnapError(
          'context_invalid',
          SnapDenyCodes.ContextInvalid,
          'Application context exceeds maximum key count.'
        );
      }
      result[k] = walk(v, depth + 1);
    }
    return result;
  };

  try {
    const cleaned = walk(input, 0) as Record<string, unknown>;
    Object.assign(out, cleaned);
  } catch (err) {
    if (err && typeof err === 'object' && 'kind' in err) {
      return { ok: false, error: err as ReturnType<typeof createSnapError> };
    }
    return {
      ok: false,
      error: createSnapError(
        'context_invalid',
        SnapDenyCodes.ContextInvalid,
        'Failed to sanitize application context.'
      )
    };
  }

  let json: string;
  try {
    json = JSON.stringify(out);
  } catch {
    return {
      ok: false,
      error: createSnapError(
        'context_invalid',
        SnapDenyCodes.ContextInvalid,
        'Application context is not serializable.'
      )
    };
  }

  const bytes = new TextEncoder().encode(json).length;
  if (bytes > MAX_CONTEXT_BYTES) {
    return {
      ok: false,
      error: createSnapError(
        'context_invalid',
        SnapDenyCodes.ContextInvalid,
        `Application context exceeds ${MAX_CONTEXT_BYTES} bytes.`
      )
    };
  }

  return { ok: true, context: out, bytes };
}

export interface BrowserObservedContext {
  pageUrl?: string;
  pageTitle?: string;
  viewportWidth: number;
  viewportHeight: number;
  userAgentSummary?: string;
  capturedAtUtc: string;
  correlationId: string;
}

export function collectBrowserContext(opts: {
  includePageUrl: boolean;
  includeQueryString: boolean;
  includePageTitle: boolean;
  correlationId: string;
  now?: () => Date;
}): BrowserObservedContext {
  const now = opts.now?.() ?? new Date();
  const result: BrowserObservedContext = {
    viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
    viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
    capturedAtUtc: now.toISOString(),
    correlationId: opts.correlationId
  };

  if (typeof document !== 'undefined' && opts.includePageTitle) {
    result.pageTitle = document.title || undefined;
  }

  if (typeof location !== 'undefined' && opts.includePageUrl) {
    try {
      const url = new URL(location.href);
      if (!opts.includeQueryString) {
        url.search = '';
        url.hash = '';
      }
      result.pageUrl = url.toString();
    } catch {
      result.pageUrl = undefined;
    }
  }

  if (typeof navigator !== 'undefined') {
    const ua = navigator.userAgent || '';
    result.userAgentSummary = ua.slice(0, 120);
  }

  return result;
}

/** Merge layers without allowing overrides of server-authoritative fields. */
export function buildSubmissionContext(args: {
  server: SnapTenantSolutionContext;
  application: Record<string, unknown>;
  browser: BrowserObservedContext;
}): {
  serverAuthoritative: SnapTenantSolutionContext;
  applicationHints: Record<string, unknown>;
  browserObserved: BrowserObservedContext;
} {
  return {
    serverAuthoritative: {
      organisationUid: args.server.organisationUid,
      solutionUid: args.server.solutionUid,
      userUid: args.server.userUid ?? null,
      environmentUid: args.server.environmentUid ?? null,
      componentUid: args.server.componentUid ?? null,
      runtimeRegistrationUid: args.server.runtimeRegistrationUid ?? null
    },
    applicationHints: { ...args.application },
    browserObserved: { ...args.browser }
  };
}
